'use client';

import { useState, useCallback } from 'react';
import { SelectedRange } from '@/modules/shared/DataLayers/types';

/**
 * useSelectionMode
 *
 * A React hook that manages selection mode state and range selections across multiple charts.
 * This is typically used in dashboards with interactive time-series charts where users can
 * draw and synchronize selections (time intervals) across views.
 *
 * Each selection is associated with:
 * - a global selection ID (shared across charts for sync)
 * - a chart-specific ID (to distinguish per-chart selection markers)
 *
 * @returns An object containing:
 * - `selectionMode`: boolean state indicating if selection mode is active
 * - `selectedRanges`: list of current selection ranges
 * - `toggleSelectionMode()`: toggles selection mode on/off
 * - `addSelectedRange(startTime, endTime, chartId)`: adds or replaces a time range for a chart
 * - `removeSelectedRange(id)`: removes all ranges associated with a global selection ID
 * - `clearAllSelections()`: removes all selections and resets the global selection ID
 * - `syncSelectionToChart(chartId)`: clones an existing selection into a chart if missing
 * - `globalSelectionId`: the active global selection group identifier (if any)
 */
export const useSelectionMode = () => {
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRanges, setSelectedRanges] = useState<SelectedRange[]>([]);
  const [globalSelectionId, setGlobalSelectionId] = useState<string | null>(null);

  /**
   * Toggles selection mode on or off.
   */
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
  }, []);

  /**
   * Adds a selected time range to a specific chart.
   * If no global selection ID exists, one is generated.
   * Any previous ranges using the same global ID are removed before adding the new one.
   *
   * @param startTime - Start of the selected time range.
   * @param endTime - End of the selected time range.
   * @param chartId - ID of the chart receiving the range.
   */
  const addSelectedRange = useCallback(
    (startTime: Date, endTime: Date, chartId: string) => {
      const selectionId = globalSelectionId || `selection-${Date.now()}`;

      if (!globalSelectionId) {
        setGlobalSelectionId(selectionId);
      }

      setSelectedRanges((prev) => {
        const filtered = prev.filter((range) => !range.id.startsWith(selectionId));

        const newRange: SelectedRange = {
          id: `${selectionId}-${chartId}`,
          startTime,
          endTime,
          chartId,
          label: `Selection ${filtered.length / 3 + 1}`,
          globalId: selectionId,
        };

        return [...filtered, newRange];
      });
    },
    [globalSelectionId]
  );

  /**
   * Removes all selected ranges associated with the provided range ID's global group.
   * Also resets the global selection ID if applicable.
   *
   * @param id - The chart-specific selection ID to remove (used to derive the global group).
   */
  const removeSelectedRange = useCallback(
    (id: string) => {
      const parts = id.split('-');
      const globalId = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : id;

      setSelectedRanges((prev) => prev.filter((range) => !range.id.startsWith(globalId)));

      if (globalSelectionId && globalId.startsWith(globalSelectionId)) {
        setGlobalSelectionId(null);
      }
    },
    [globalSelectionId]
  );

  /**
   * Clears all selected ranges across all charts and resets selection state.
   */
  const clearAllSelections = useCallback(() => {
    setSelectedRanges([]);
    setGlobalSelectionId(null);
  }, []);

  /**
   * Synchronizes the current global selection to a target chart,
   * cloning the selection into that chart if not already present.
   *
   * @param chartId - The chart ID to apply the selection to.
   */
  const syncSelectionToChart = useCallback(
    (chartId: string) => {
      if (!globalSelectionId || selectedRanges.length === 0) return;

      const existingSelection = selectedRanges.find((range) => range.globalId === globalSelectionId);

      if (existingSelection) {
        const hasSelection = selectedRanges.some(
          (range) => range.chartId === chartId && range.globalId === globalSelectionId
        );

        if (!hasSelection) {
          const newRange: SelectedRange = {
            id: `${globalSelectionId}-${chartId}`,
            startTime: existingSelection.startTime,
            endTime: existingSelection.endTime,
            chartId,
            label: existingSelection.label,
            globalId: globalSelectionId,
          };

          setSelectedRanges((prev) => [...prev, newRange]);
        }
      }
    },
    [globalSelectionId, selectedRanges]
  );

  return {
    selectionMode,
    selectedRanges,
    toggleSelectionMode,
    addSelectedRange,
    removeSelectedRange,
    clearAllSelections,
    syncSelectionToChart,
    globalSelectionId,
  };
};
